import ApiInstance from '../../interfaces/api-instance';
import { getSessionTokenFromUrl } from '../../utilis/session-token';
import { REALTIME_ROUTES } from './route';
// Temporary developer-only observability (network connectivity investigation,
// Step 3). A no-op unless FEATURES.NETWORK_DIAGNOSTICS — see the store for the full rationale.
// Not much to add here beyond the retry counter: createRealtimeCall and
// registerCallId both go through ApiInstance, which already times/sizes every
// call generically (interfaces/api-instance.js) — there is no more direct
// browser->OpenAI fetch in this file to instrument separately (see route.js).
import { useNetworkDiagnostics } from '../../zustand/network-diagnostics-store';

// The backend maps OpenAI's failure onto a status of its own: 401 bad key,
// 403 region block, 429 quota, 502/503 outage. Only the transient two are worth
// another attempt — a 401/403 will fail identically however many times we ask.
const RETRYABLE_STATUSES = new Set([429, 503]);
const CALL_RETRY_DELAYS_MS = [400, 1200];

/**
 * Turns an axios failure into an error the UI can actually show.
 *
 * The backend's error envelope is `{error, openai_error_code, openai_error_type}`,
 * and `error` is the human-readable part — without this the store would surface
 * axios's "Request failed with status code 429" instead of "quota exceeded".
 */
export function normalizeRealtimeError(error) {
  const status = error?.response?.status ?? null;
  const body = error?.response?.data ?? null;
  const message =
    (typeof body?.error === 'string' && body.error) ||
    (typeof body?.detail === 'string' && body.detail) ||
    error?.message ||
    'Realtime request failed';

  const normalized = new Error(message);
  normalized.name = 'RealtimeApiError';
  normalized.status = status;
  normalized.openaiErrorCode = body?.openai_error_code ?? body?.openaiErrorCode ?? null;
  normalized.openaiErrorType = body?.openai_error_type ?? body?.openaiErrorType ?? null;
  // No status means the request never reached the backend (abort, offline). Not
  // retried here: an abort is deliberate, and an offline retry loop just delays
  // the error the caller needs to see.
  normalized.isRetryable = status !== null && RETRYABLE_STATUSES.has(status);
  normalized.cause = error;

  return normalized;
}

function waitBeforeRetry(delayMs, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error('Realtime call retry aborted'));
      return;
    }

    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error('Realtime call retry aborted'));
    };

    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);

    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/**
 * Opens the Realtime call — through our backend, never straight to OpenAI.
 *
 * The browser hands over its SDP offer; the backend posts it to
 * `/v1/realtime/calls` with the **server API key** and returns OpenAI's SDP
 * answer plus the `rtc_…` call id.
 *
 * That detour is the whole point, not a proxy for its own sake: a call created
 * with an ephemeral `ek_…` cannot be attached to by the backend's sideband —
 * OpenAI answers `404 call_id_not_found` to every attach for the life of the
 * call, even though the call itself works. Only a call created with the API key
 * can be driven server-side, and the API key can never be handed to a browser.
 * The old two-step (mint `ek_…`, POST the SDP from here) is gone with it.
 *
 * `options.openaiRealtimeModel` is the session's picked model (prescription
 * builder). It belongs on this request and nowhere else: the model is bound to
 * the call when it is created, so this is the only moment it can be chosen.
 * Omitted when nothing was picked, so the backend keeps its own default.
 *
 * Returns `{ callId, answerSdp }` — the backend also caches the call id itself,
 * so nothing further is needed before the sideband can attach.
 */
export async function createRealtimeCall(sdp, sessionId, options = {}) {
  const body = {
    sdp,
    sessionId: sessionId ?? null,
    sessionToken: getSessionTokenFromUrl(),
    ...(options.openaiRealtimeModel ? { model: options.openaiRealtimeModel } : {}),
  };

  for (let attempt = 0; ; attempt += 1) {
    try {
      const { data } = await ApiInstance.post(REALTIME_ROUTES.CALL, body, {
        headers: { 'Content-Type': 'application/json' },
        signal: options.signal,
        // A 401 here means OpenAI rejected the backend's key, not that the
        // patient's session expired. Without this the shared interceptor
        // would bounce them to /login mid-exercise.
        skipAuthRedirect: true,
      });

      const callId = data?.callId ?? data?.call_id ?? null;
      const answerSdp = data?.sdp ?? data?.answerSdp ?? null;

      if (!callId) {
        throw new Error('No call id in the realtime call response');
      }
      if (!answerSdp) {
        throw new Error('No SDP answer in the realtime call response');
      }

      return { callId, answerSdp };
    } catch (error) {
      const normalized = normalizeRealtimeError(error);
      const delayMs = CALL_RETRY_DELAYS_MS[attempt];

      if (!normalized.isRetryable || delayMs === undefined) {
        throw normalized;
      }

      useNetworkDiagnostics.getState().record('rest', 'request_retry', attempt + 1, 'count', {
        endpoint: REALTIME_ROUTES.CALL,
      });

      await waitBeforeRetry(delayMs, options.signal);
    }
  }
}

/**
 * Confirms the call id once the peer connection has actually reached
 * `connected`. The backend already cached it when it opened the call, so this is
 * a confirmation rather than the handoff it used to be — and it is still what
 * re-points a running sideband after a reconnect built a new call.
 *
 * Both fields are required — a missing one is a 400, not a silent no-op.
 */
export async function registerCallId(callId, sessionId, exerciseId, options = {}) {
  try {
    const { data } = await ApiInstance.post(
      REALTIME_ROUTES.REGISTER_CALL,
      {
        callId,
        sessionId: sessionId ?? null,
        exerciseId: exerciseId ?? null,
        sessionToken: getSessionTokenFromUrl(),
      },
      {
        headers: { 'Content-Type': 'application/json' },
        signal: options.signal,
        skipAuthRedirect: true,
      }
    );

    return data;
  } catch (error) {
    throw normalizeRealtimeError(error);
  }
}

export const realtimeSessionApi = {
  createRealtimeCall,
  registerCallId,
};
